import type { Turma, Turno } from '../../../types/models';

export const SERIES_OPTIONS = [
  { group: 'Educação Infantil', items: ['Maternal', 'Jardim I', 'Jardim II'] },
  { group: 'Fund. I', items: ['1º Ano', '2º Ano', '3º Ano', '4º Ano', '5º Ano'] },
  { group: 'Fund. II', items: ['6º Ano', '7º Ano', '8º Ano', '9º Ano'] },
  { group: 'Médio', items: ['1ª Série', '2ª Série', '3ª Série'] },
];

export const NIVEL_FULL: Record<string, string> = {
  'Educação Infantil': 'Educação Infantil',
  'Fund. I': 'Ensino Fundamental I',
  'Fund. II': 'Ensino Fundamental II',
  'Médio': 'Ensino Médio',
};

/** Ordem canônica das séries para agrupamento */
const SERIE_ORDER: Record<string, number> = {};
let _idx = 0;
SERIES_OPTIONS.forEach(g => g.items.forEach(s => { SERIE_ORDER[s] = _idx++; }));

export function serieOrder(serie: string): number {
  return SERIE_ORDER[serie] ?? 999;
}

/** Nível de ensino da série (mesmo agrupamento do select de Série). */
export function nivelDaSerieLabel(serie: string): string {
  return SERIES_OPTIONS.find((g) => g.items.includes(serie))?.group ?? 'Outras';
}

/** Agrupa turmas por nível → série, respeitando a ordem canônica */
export function agruparPorSerie(turmas: Turma[]): { nivel: string; serie: string; turmas: Turma[] }[] {
  const map = new Map<string, { nivel: string; serie: string; turmas: Turma[] }>();
  for (const t of turmas) {
    const nivel = SERIES_OPTIONS.find(g => g.items.includes(t.serie))?.group ?? 'Outras';
    const key = t.serie;
    if (!map.has(key)) map.set(key, { nivel, serie: t.serie, turmas: [] });
    map.get(key)!.turmas.push(t);
  }
  return [...map.values()].sort((a, b) => serieOrder(a.serie) - serieOrder(b.serie));
}

/** Agrupa turmas primeiro por nível de ensino, depois por série dentro de cada nível */
export function agruparPorNivel(turmas: Turma[]): { nivel: string; series: { serie: string; turmas: Turma[] }[] }[] {
  const nivelMap = new Map<string, Map<string, Turma[]>>();
  for (const t of turmas) {
    const nivel = SERIES_OPTIONS.find(g => g.items.includes(t.serie))?.group ?? 'Outras';
    if (!nivelMap.has(nivel)) nivelMap.set(nivel, new Map());
    const serieMap = nivelMap.get(nivel)!;
    if (!serieMap.has(t.serie)) serieMap.set(t.serie, []);
    serieMap.get(t.serie)!.push(t);
  }
  // Ordena níveis pela ordem dos SERIES_OPTIONS
  const nivelOrder = SERIES_OPTIONS.reduce((acc, g, i) => { acc[g.group] = i; return acc; }, {} as Record<string, number>);
  return [...nivelMap.entries()]
    .sort(([a], [b]) => (nivelOrder[a] ?? 99) - (nivelOrder[b] ?? 99))
    .map(([nivel, serieMap]) => ({
      nivel,
      series: [...serieMap.entries()]
        .sort(([a], [b]) => serieOrder(a) - serieOrder(b))
        .map(([serie, turmas]) => ({ serie, turmas: turmas.sort((a, b) => a.nome.localeCompare(b.nome)) })),
    }));
}

/** Detecta turno preferido pela hora do dia */
export function turnoPreferidoPorHora(turnosDoAno: Turno[]): Turno | undefined {
  if (!turnosDoAno.length) return undefined;
  const hora = new Date().getHours();
  const prefixo = hora < 12 ? 'manh' : hora < 18 ? 'tarde' : 'noturno';
  return (
    turnosDoAno.find(t => t.nome.toLowerCase().includes(prefixo)) ??
    turnosDoAno[0]
  );
}

/** Classifica um ano em relação ao ano atual */
export function tipoAno(ano: number, anoAtual: number): 'passado' | 'atual' | 'futuro' {
  if (ano < anoAtual) return 'passado';
  if (ano === anoAtual) return 'atual';
  return 'futuro';
}

/**
 * Rótulo curto da turma na lista (coluna Turma), sem repetir a série.
 * Ex.: série "1º Ano" + nome "1º Ano A" → "Turma A"
 */
export function rotuloTurmaCurto(turma: {
  nome?: string | null;
  serie?: string | null;
  sala?: string | null;
}): string {
  const serie = (turma.serie || '').trim();
  const nome = (turma.nome || '').trim();
  let sufixo = '';
  if (serie && nome.toLowerCase().startsWith(serie.toLowerCase())) {
    sufixo = nome.slice(serie.length).trim().replace(/^[-–]\s*/, '');
  }
  if (!sufixo && turma.sala?.trim()) sufixo = turma.sala.trim();
  if (!sufixo) {
    const parts = nome.split(/\s+/).filter(Boolean);
    sufixo = parts[parts.length - 1] || nome;
  }
  if (!sufixo) return '—';
  if (/^turma\b/i.test(sufixo)) return sufixo;
  return `Turma ${sufixo}`;
}

export type ChamadaAulaBadge = { numero: number; feita: boolean };

/** Monta badges 1..N por turma a partir da grade do dia + status das chamadas. */
export function buildChamadaBadgesPorTurma(
  horariosHoje: Array<{
    id?: string;
    turmaId?: string | null;
    tipo?: string | null;
    numeroAula?: number | null;
  }>,
  chamadasDia: Array<{
    id?: string;
    turmaId: string;
    horarioAulaId?: string | null;
    numeroAula?: number | null;
    status: string;
  }>,
): Map<string, ChamadaAulaBadge[]> {
  const feitas = new Set<string>();
  for (const c of chamadasDia) {
    const st = (c.status || '').toUpperCase();
    if (st !== 'CONCLUIDA' && st !== 'ABERTA') continue;
    if (c.horarioAulaId) feitas.add(`h:${c.horarioAulaId}`);
    if (c.numeroAula != null && c.numeroAula > 0) {
      feitas.add(`n:${c.turmaId}:${c.numeroAula}`);
    }
  }

  const numsPorTurma = new Map<string, Map<number, string | undefined>>();
  for (const h of horariosHoje) {
    if ((h.tipo || 'AULA') !== 'AULA') continue;
    if (!h.turmaId || h.numeroAula == null || h.numeroAula <= 0) continue;
    let byNum = numsPorTurma.get(h.turmaId);
    if (!byNum) {
      byNum = new Map();
      numsPorTurma.set(h.turmaId, byNum);
    }
    if (!byNum.has(h.numeroAula)) byNum.set(h.numeroAula, h.id);
  }

  const out = new Map<string, ChamadaAulaBadge[]>();
  for (const [turmaId, byNum] of numsPorTurma) {
    const badges = [...byNum.entries()]
      .sort((a, b) => a[0] - b[0])
      .map(([numero, slotId]) => ({
        numero,
        feita:
          (slotId ? feitas.has(`h:${slotId}`) : false) ||
          feitas.has(`n:${turmaId}:${numero}`),
      }));
    out.set(turmaId, badges);
  }
  return out;
}
